import type { IconName, SelectOption } from '@camelot/components'
import type { Address, GroupedSelectOption } from '~/types/location'
import type { StationType, VirtualStation } from '~/types/virtual-station'
import { translate } from '~/composables/translate/translations'

/**
 * Map station type to icon name
 */
function getIconForStationType(type: StationType): IconName {
  const iconMap: Record<StationType, IconName> = {
    airport: 'airport',
    train_station: 'train',
    city_station: 'hotel',
  }
  return iconMap[type] || 'location' // Fallback to location icon
}

/**
 * Map station type to translated group label
 */
function getGroupLabelForType(type: StationType): string {
  const labelMap: Record<StationType, string> = {
    airport: translate('virtual_stations_group_airports'),
    train_station: translate('virtual_stations_group_train_stations'),
    city_station: translate('virtual_stations_group_cities'),
  }
  return labelMap[type]
}

/**
 * Convert VirtualStation to SelectOption with proper icon and Address value
 */
export function mapVirtualStationToSelectOption(station: VirtualStation): SelectOption {
  const value: Address = {
    info: station.label,
    deliveryAddress: station.address,
    location: {
      latitude: station.coordinates.latitude,
      longitude: station.coordinates.longitude,
    },
  }

  return {
    prefixIcon: getIconForStationType(station.type),
    label: station.label,
    value,
  }
}

/**
 * Group virtual stations by type and convert to grouped SelectOptions
 * Returns in order: Airports, Train stations, Cities
 * Entries within each group are sorted alphabetically by label.
 */
export function groupVirtualStations(stations: VirtualStation[]): GroupedSelectOption[] {
  const typeOrder: StationType[] = ['airport', 'train_station', 'city_station']

  // Group stations by type
  const grouped = stations.reduce((acc, station) => {
    if (!acc[station.type]) {
      acc[station.type] = []
    }
    acc[station.type].push(station)
    return acc
  }, {} as Record<StationType, VirtualStation[]>)

  // Build grouped options in order
  return typeOrder
    .filter(type => grouped[type] && grouped[type].length > 0) // Only include types with stations
    .map(type => ({
      label: getGroupLabelForType(type),
      options: grouped[type]
        .slice()
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(mapVirtualStationToSelectOption),
    }))
}
